/**
 * computeGroupClasses(calendarData)
 *
 * Given the full availability data for a month (keyed by YYYY-MM-DD string),
 * returns a map of dateStr → position classification for rendering connected
 * date range visual groups.
 *
 * Classifications:
 *   'group-start'  — first date in a multi-date group
 *   'group-middle' — interior date in a multi-date group
 *   'group-end'    — last date in a multi-date group
 *   'solo'         — ungrouped, or single-date group (renders with full rounding)
 *
 * Grouping key priority:
 *   1. block_group_id (manual and booking groups)
 *   2. recurringBlockId (recurring blocks, client-side grouping)
 *   3. null / undefined → 'solo'
 *
 * SCRUM-1152 B-NEW-05
 */

/**
 * Get the previous day's YYYY-MM-DD string
 */
function getPreviousDay(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d - 1);
  const py = date.getFullYear();
  const pm = String(date.getMonth() + 1).padStart(2, '0');
  const pd = String(date.getDate()).padStart(2, '0');
  return `${py}-${pm}-${pd}`;
}

/**
 * Get the next day's YYYY-MM-DD string
 */
function getNextDay(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d + 1);
  const ny = date.getFullYear();
  const nm = String(date.getMonth() + 1).padStart(2, '0');
  const nd = String(date.getDate()).padStart(2, '0');
  return `${ny}-${nm}-${nd}`;
}

/**
 * Extract the grouping key from a calendar data entry.
 * block_group_id takes priority over recurringBlockId.
 */
function getGroupKey(row) {
  return row.block_group_id || row.blockGroupId || row.recurringBlockId || null;
}

/**
 * Compute group position classifications for each date in calendarData.
 *
 * @param {Object} calendarData - Date-keyed object: { 'YYYY-MM-DD': { block_group_id?, recurringBlockId?, ... } }
 * @returns {Object} Date-keyed object: { 'YYYY-MM-DD': 'group-start' | 'group-middle' | 'group-end' | 'solo' }
 */
export function computeGroupClasses(calendarData) {
  const result = {};

  for (const dateStr of Object.keys(calendarData)) {
    const row = calendarData[dateStr];
    const groupKey = getGroupKey(row);

    if (!groupKey) {
      result[dateStr] = 'solo';
      continue;
    }

    // Check previous day — same group key?
    const prevDate = getPreviousDay(dateStr);
    const prevRow = calendarData[prevDate];
    const hasPrev = prevRow && getGroupKey(prevRow) === groupKey;

    // Check next day — same group key?
    const nextDate = getNextDay(dateStr);
    const nextRow = calendarData[nextDate];
    const hasNext = nextRow && getGroupKey(nextRow) === groupKey;

    if (!hasPrev && !hasNext) {
      result[dateStr] = 'solo';
    } else if (!hasPrev && hasNext) {
      result[dateStr] = 'group-start';
    } else if (hasPrev && hasNext) {
      result[dateStr] = 'group-middle';
    } else {
      result[dateStr] = 'group-end';
    }
  }

  return result;
}
